import { baseApi } from '@/api/baseApi';
import type {
    GetPaymentResponse,
    ListPaymentsFilters,
    ListPaymentsResponse,
    RefundPaymentRequest,
    RefundPaymentResponse
} from '@/lib/paymentsSchemas';
import {
    GetPaymentResponseSchema,
    ListPaymentsResponseSchema,
    RefundPaymentResponseSchema
} from '@/lib/paymentsSchemas';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /payments/*.
 * admin-backend на порту 5002 без префикса /admin.
 */
export const paymentsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listPayments: build.query<ListPaymentsResponse, ListPaymentsFilters | void>({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.search !== undefined && params.search !== '') {
                    search.search = params.search;
                }
                if (params?.userId) {
                    search.userId = params.userId;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/payments',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): ListPaymentsResponse =>
                parseWithSchema(ListPaymentsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((p) => ({
                            type: 'Payment' as const,
                            id: p.id
                        })),
                        { type: 'Payment' as const, id: 'LIST' }
                    ]
                    : [{ type: 'Payment' as const, id: 'LIST' }]
        }),

        getPayment: build.query<GetPaymentResponse, { id: string }>({
            query: ({ id }) => ({
                url: `/payments/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetPaymentResponse =>
                parseWithSchema(GetPaymentResponseSchema, raw),
            providesTags: (_result, _error, arg) => [{ type: 'Payment' as const, id: arg.id }]
        }),

        refundPayment: build.mutation<
            RefundPaymentResponse,
            { id: string; body: RefundPaymentRequest }
        >({
            query: ({ id, body }) => ({
                url: `/payments/${id}/refund`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): RefundPaymentResponse =>
                parseWithSchema(RefundPaymentResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'Payment', id: arg.id },
                { type: 'Payment', id: 'LIST' },
                { type: 'Subscription', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListPaymentsQuery,
    useGetPaymentQuery,
    useRefundPaymentMutation
} = paymentsApi;
